import { tracked } from "@glimmer/tracking";
import { computed } from "@ember/object";
import { ajax } from "discourse/lib/ajax";
import { popupAjaxError } from "discourse/lib/ajax-error";
import { getOwnerWithFallback } from "discourse/lib/get-owner";
import Group from "discourse/models/group";
import { i18n } from "discourse-i18n";
import ModelAttribute from "../lib/model-attribute";
import AttributesModel from "./attributes";

export default class Circle extends AttributesModel {
  static PRIVACY_LEVELS = {
    public: {
      value: 0,
      icon: "discourse_circles_public_circle",
    },
    private: {
      value: 1,
      icon: "discourse_circles_private_circle",
    },
  };

  static #attributes;
  static #customFieldAttributes;

  static attributes() {
    const siteSettings = getOwnerWithFallback(this).lookup(
      "service:site-settings"
    );

    if (!this.#attributes) {
      this.#attributes = Object.freeze([
        new ModelAttribute("name", {
          displayName: "discourse_circles.circle_name",
          validation: {
            async: true,
            presence: true,
            minLength: siteSettings.min_username_length,
            maxLength: siteSettings.discourse_circles_max_circle_name_length,
            custom: async (value, _, options) => {
              const { updateStatus, model } = options;

              let success = null;
              const errors = [];

              // return early if the name hasn't changed
              if (model?.name === value) {
                return { success: true };
              }

              // query the server to verify if the name is available
              try {
                updateStatus?.(
                  "loading",
                  i18n("discourse_circles.form.name.checking")
                );

                const response = await this.checkName(value);
                if (response.available) {
                  success = i18n("discourse_circles.form.name.available");
                } else {
                  if (response.errors) {
                    errors.push(...response.errors);
                  } else {
                    errors.push(
                      i18n("discourse_circles.form.name.not_available")
                    );
                  }
                }
              } catch (error) {
                errors.push(i18n("discourse_circles.form.validation_error"));
                popupAjaxError(error);
              }

              return { success, errors };
            },
          },
        }),
        new ModelAttribute("definition_raw", {
          displayName: "discourse_circles.circle_definition",
          validation: {
            presence: siteSettings.discourse_circles_require_description,
            minLength: siteSettings.discourse_circles_require_description
              ? siteSettings.min_first_post_length
              : 0,
            maxLength:
              siteSettings.discourse_circles_max_circle_definition_length,
          },
        }),
        new ModelAttribute("privacy_level", {
          defaultValue: this.PRIVACY_LEVELS.public.value,
          validation: {
            presence: true,
          },
        }),
        new ModelAttribute("mentionable_level", {
          defaultValue:
            siteSettings.discourse_circles_default_mentionable_level,
        }),
        new ModelAttribute("uploaded_logo"),
        new ModelAttribute("uploaded_logo_dark"),
        new ModelAttribute("uploaded_background"),
        new ModelAttribute("uploaded_background_dark"),
      ]);
    }

    return [...this.#attributes, ...this.customFieldAttributes()];
  }

  static customFieldAttributes() {
    return this.#customFieldAttributes || [];
  }

  static updateCustomFieldsAttributes(customFieldDescriptors) {
    if (!customFieldDescriptors) {
      return;
    }

    this.#customFieldAttributes = customFieldDescriptors.map((field) => {
      return new ModelAttribute(`field_${field.id}`, {
        isCustomField: true,
        id: field.id,
        displayName: field.name,
        description: field.description,
        fieldType: field.field_type,
        editable: field.editable,
        searchable: field.searchable,
        position: field.position,
        options: field.options, // in this context options is the list of items used in select fields
        displayOnAboutPage: field.display_on_about_page,
        displayOnIndexHeader: field.display_on_index_header,
        displayOnCard: field.display_on_card,
        validation: {
          presence: field.required,
        },
      });
    });

    return this.#customFieldAttributes;
  }

  static munge(json) {
    const store = getOwnerWithFallback(this).lookup("service:store");

    if (json?.category) {
      const site = getOwnerWithFallback(this).lookup("service:site");
      const category = site.updateCategory(json.category);

      if (category.topics) {
        category.topics = category.topics.map((t) =>
          store.createRecord("topic", t)
        );
      }

      json.category = category;
    }

    if (json?.staff_group) {
      json.staff_group = Group.create(json.staff_group);
    }
    if (json?.member_group) {
      json.member_group = Group.create(json.member_group);
    }

    this.customFieldAttributes().forEach((field) => {
      const fieldId = field.options.id;

      if (json?.custom_fields?.[fieldId]) {
        json[`field_${fieldId}`] = json.custom_fields[fieldId];
      }
    });

    return json;
  }

  static checkName(name) {
    return ajax("/circles/check-name", { data: { name } });
  }

  static async handleMembershipRequest(categoryId, data) {
    return ajax(`/circles/${categoryId}/handle_membership_request.json`, {
      data,
      type: "PUT",
    });
  }

  @tracked category_id;
  @tracked name;
  @tracked slug;
  @tracked privacy_level;
  @tracked is_circle_member;
  @tracked can_create_topic;
  @tracked owners;
  @tracked member_group;
  @tracked staff_group;
  @tracked category;
  @tracked _uploaded_logo;
  @tracked _uploaded_logo_dark;
  @tracked _uploaded_background;
  @tracked _uploaded_background_dark;

  get isPublic() {
    return this.privacy_level === Circle.PRIVACY_LEVELS.public.value;
  }

  get isPrivate() {
    return this.privacy_level === Circle.PRIVACY_LEVELS.private.value;
  }

  get hasLogo() {
    return !!this.category?.uploaded_logo?.url;
  }

  get hasBackground() {
    return !!this.category?.uploaded_background?.url;
  }

  get uploaded_logo() {
    return this._uploaded_logo;
  }

  set uploaded_logo(value) {
    const { id, url, width, height } = value || {};
    const uploadedLogo = new UploadedImage();

    uploadedLogo.id = id;
    uploadedLogo.url = url;
    uploadedLogo.width = width;
    uploadedLogo.height = height;

    this._uploaded_logo = uploadedLogo;
  }

  get uploaded_logo_dark() {
    return this._uploaded_logo_dark;
  }

  set uploaded_logo_dark(value) {
    const { id, url, width, height } = value || {};
    const uploadedLogoDark = new UploadedImage();

    uploadedLogoDark.id = id;
    uploadedLogoDark.url = url;
    uploadedLogoDark.width = width;
    uploadedLogoDark.height = height;

    this._uploaded_logo_dark = uploadedLogoDark;
  }

  get uploaded_background() {
    return this._uploaded_background;
  }

  set uploaded_background(value) {
    const { id, url, width, height } = value || {};
    const uploadedBackground = new UploadedImage();

    uploadedBackground.id = id;
    uploadedBackground.url = url;
    uploadedBackground.width = width;
    uploadedBackground.height = height;

    this._uploaded_background = uploadedBackground;
  }

  get uploaded_background_dark() {
    return this._uploaded_background_dark;
  }

  set uploaded_background_dark(value) {
    const { id, url, width, height } = value || {};
    const uploadedBackgroundDark = new UploadedImage();

    uploadedBackgroundDark.id = id;
    uploadedBackgroundDark.url = url;
    uploadedBackgroundDark.width = width;
    uploadedBackgroundDark.height = height;

    this._uploaded_background_dark = uploadedBackgroundDark;
  }

  get groups() {
    return [this.member_group, this.staff_group];
  }

  @computed("category_id", "slug")
  get slug_path_with_id() {
    if (!this.slug) {
      return this.category_id;
    }

    return `${this.slug}/${this.category_id}`;
  }

  updateFromJson(json) {
    this.setProperties(this.constructor.munge(json));

    return this;
  }

  async reload() {
    if (this.category_id === null) {
      return;
    }

    const json = await ajax(`/circles/${this.slug_path_with_id}.json`, {
      type: "GET",
    });

    this.updateFromJson(json.circle);
  }

  save(data = {}) {
    const {
      name,
      definition_raw,
      privacy_level,
      uploaded_logo,
      uploaded_logo_dark,
      uploaded_background,
      uploaded_background_dark,
      ...opts
    } = data;
    const isInserting = this.category_id == null;

    return ajax(isInserting ? "/circles.json" : `${this.#apiPath}.json`, {
      type: isInserting ? "POST" : "PUT",
      data: {
        name,
        definition_raw,
        privacy_level,
        uploaded_logo_id: uploaded_logo?.id,
        uploaded_logo_dark_id: uploaded_logo_dark?.id,
        uploaded_background_id: uploaded_background?.id,
        uploaded_background_dark_id: uploaded_background_dark?.id,
        ...opts,
      },
    }).then((response) => {
      this.updateFromJson(response.circle);
      return this;
    });
  }

  destroy() {
    if (!this.category_id) {
      return;
    }
    return ajax(`${this.#apiPath}.json`, { type: "DELETE" });
  }

  async join() {
    await ajax(`/groups/${this.member_group.id}/join.json`, {
      type: "PUT",
    });
    await this.member_group.reloadMembers({}, true);
  }

  async addMembers(usernames, staff = false, notifyUsers = true) {
    return await ajax(`${this.#apiPath}/members.json`, {
      type: "POST",
      data: { usernames, notifyUsers, staff },
    });
  }

  async changeOwnership(mode, params) {
    let requestType;

    switch (mode) {
      case "add":
        requestType = "POST";
        break;
      case "remove":
        requestType = "DELETE";
        break;
      case "transfer":
        requestType = "PUT";
        break;
      default:
        throw new Error(`circle#changeOwnership mode ${mode} is unknown`);
    }

    const response = await ajax(`${this.#apiPath}/ownership`, {
      type: requestType,
      data: params,
    });
    const { circle: updatedCircle } = response;

    this.updateFromJson(updatedCircle);
  }

  async requestMembership(reason) {
    return ajax(`${this.#apiPath}/request_membership.json`, {
      type: "POST",
      data: { reason },
    });
  }

  async leave() {
    if (!this.category_id) {
      return;
    }

    await ajax(`${this.#apiPath}/leave.json`, {
      type: "DELETE",
    });

    await this.reload();
  }

  async cancelMembershipRequest() {
    if (!this.category_id) {
      return;
    }

    await ajax(`${this.#apiPath}/cancel_membership_request.json`, {
      type: "DELETE",
    });

    await this.reload();
  }

  async reloadCustomFields() {
    await this.constructor.reloadCustomFields();
  }

  get #apiPath() {
    return `/circles/${this.category_id}`;
  }

  get customFields() {
    if (!this.custom_fields || Object.keys(this.custom_fields).length === 0) {
      return [];
    }

    return Circle.customFieldAttributes().map((r) => ({
      value: this[r.name],
      name: r.displayName,
      type: r.options.fieldType,
      displayOnAboutPage: r.options.displayOnAboutPage,
      displayOnCard: r.options.displayOnCard,
      displayOnIndexHeader: r.options.displayOnIndexHeader,
    }));
  }
}

class UploadedImage {
  @tracked id;
  @tracked url;
  @tracked width;
  @tracked height;

  get aspectRatio() {
    return this.width / this.height;
  }
}
